import {
  ApplyMaterialUpdate,
  InspectionOptimizerModel,
  IOKpis,
  PredictModel,
  PredictUpdateMetrics,
  IOMaterialUpdateMetrics,
} from '@cast/leadcast-types';
import { BaseService, BaseServiceConstructor } from 'api/services/base.service';
import qs from 'qs';
import { MRT_PaginationState } from 'material-react-table';
import { IODataTableRecord } from 'types/models/IODataTableRecord';
import { PaginatedResponse } from 'types/PaginatedResponse';

export interface applyMaterialUpdateConfig {
  mlModelRunId?: string;
  inspectionOptimizerRunId?: string;
  thresholds?: {
    upperThreshold: number;
    upperThresholdMaterial: string;
    lowerThreshold: number;
  };
  updateMetaData?: {
    modelingMethod: string;
    stateProposalAccepted: string;
    stateProposalDate: string;
    approvedBy: string;
  };
}

export class MlService extends BaseService {
  constructor({ serviceUrls, token, tenantName }: BaseServiceConstructor) {
    super({ serviceUrls, token, tenantName });
  }

  async getInspectionOptimizerKpis(): Promise<IOKpis> {
    const { data } = await this.publicHttpClient.get<IOKpis>(`/inspections/optimizer/kpis`, {
      withCredentials: false,
    });
    return data;
  }

  async getInspectionOptimizerModel(): Promise<InspectionOptimizerModel> {
    const { data } = await this.publicHttpClient.get<InspectionOptimizerModel>(
      `/inspections/optimizer/current`,
      {
        withCredentials: false,
      }
    );
    return data;
  }

  async getPaginatedInspectionOptimizerDataTable(searchCriteria: {
    pagination: MRT_PaginationState;
    sort?: any;
    filters: any;
  }): Promise<PaginatedResponse<IODataTableRecord>> {
    let params: any = {
      includeDetails: true,
      'page[number]': searchCriteria.pagination.pageIndex + 1,
      'page[size]': searchCriteria.pagination.pageSize,
      sort: searchCriteria.sort,
      ...searchCriteria.filters,
    };

    const resp = await this.publicHttpClient.get<IODataTableRecord[]>(
      `/inspections/optimizer/current/properties`,
      {
        params,
        paramsSerializer: { indexes: null },
        withCredentials: false,
      }
    );

    if (resp.status === 404) {
      return {
        data: [],
        pagination: {
          prev: null,
          next: null,
          last: null,
          count: 0,
          limit: null,
        },
      };
    }
    return resp;
  }

  async downloadInspectionOptimizerOutputs(dataFormat: string): Promise<Blob> {
    const queryString = qs.stringify({ dataFormat });
    const { data } = await this.publicHttpClient.get<Blob>(
      `/inspections/optimizer/current/download?${queryString}`,
      {
        withCredentials: false,
        responseType: 'blob',
      }
    );
    return data;
  }

  async getInspectionOptimizerMaterialUpdateMetrics(
    modelRunId: string
  ): Promise<IOMaterialUpdateMetrics> {
    const { data } = await this.publicHttpClient.get<IOMaterialUpdateMetrics>(
      `/predictiveMaterialsUpdate/inspections/optimizer/${modelRunId}/updateMetrics`,
      { withCredentials: false }
    );
    return data;
  }

  async getAllInspectionOptimizerModelRuns(): Promise<InspectionOptimizerModel[]> {
    const { data } = await this.publicHttpClient.get<InspectionOptimizerModel[]>(
      `/inspections/optimizer/all`,
      {
        withCredentials: false,
      }
    );
    return data;
  }

  async getAllPredictModelRuns(): Promise<PredictModel[]> {
    const { data } = await this.publicHttpClient.get<PredictModel[]>(`/predict/all`, {
      withCredentials: false,
    });
    return data;
  }

  async getPredictUpdateCountsByThreshold(
    modelRunID: string,
    lowerThreshold: number,
    upperThreshold: number
  ): Promise<PredictUpdateMetrics> {
    const { data } = await this.publicHttpClient.post<PredictUpdateMetrics>(
      `/predictiveMaterialsUpdate/predict/${modelRunID}/updateMetrics`,
      {
        lowerThreshold,
        upperThreshold,
      },
      {
        withCredentials: false,
      }
    );
    return data;
  }

  async getPredictiveMaterialsRevertMetrics(): Promise<PredictUpdateMetrics> {
    const { data } = await this.publicHttpClient.get<PredictUpdateMetrics>(
      `/predictiveMaterialsUpdate/revertMetrics`,
      {
        withCredentials: false,
      }
    );
    return data;
  }

  async applyPredictMaterialsUpdate(
    action: 'apply' | 'revert',
    config?: applyMaterialUpdateConfig
  ): Promise<ApplyMaterialUpdate> {
    const { data } = await this.publicHttpClient.post<ApplyMaterialUpdate>(
      '/predictiveMaterialsUpdate',
      {
        action: action,
        predictiveThresholds: config?.thresholds ?? null,
        updateMetadata: config?.updateMetaData ?? null,
        mlModelRunId: config?.mlModelRunId ?? null,
        inspectionOptimizerRunId: config?.inspectionOptimizerRunId ?? null,
      },
      { withCredentials: false }
    );

    return data;
  }

  async getStrata(): Promise<string[]> {
    const { data } = await this.publicHttpClient.get<string[]>(
      `/inspections/optimizer/strata/current`,
      {
        withCredentials: false,
      }
    );
    return data;
  }
}
